import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import RouteSkeleton from "@/components/RouteSkeleton";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import SkillControlsPanel from "../SkillControlsPanel";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import IntegrationHealthBadge from "@/components/IntegrationHealthBadge";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  Divider,
  FieldInput
} from "@/components/ds";
import { FileText } from "@/components/ds/icons";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";
import { ensureDefaultRoutingWorkflow } from "@/pages/Messaging/Workflows/defaultWorkflowHelper";

const MONDAY_COLOR = "#FF3D57";

// ─── Exported nav helper ───────────────────────────────────────────────────────

export function BackToIntegrationLandingMonday() {
  const navigate = useNavigate();
  return (
    <GhostButton
      onClick={() => navigate("/integrations")}
      className="!text-zinc-600 hover:!text-zinc-800 hover:!no-underline !gap-2"
    >
      <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
      Back to Integrations
    </GhostButton>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface MondayConfig {
  id: number;
  siteId: number;
  displayName: string | null;
  accountSlug: string | null;
  defaultBoardId: string | null;
  defaultGroupId: string | null;
  pushLeadsToBoard: boolean;
  pushTranscripts: boolean;
  isEnabled: boolean;
  isConnected: boolean;
  apiTokenMasked: string | null;
  connectedAs: string | null;
  authMethod: string;
  oAuthConnectedAt: string | null;
  createdAt: string;
}

interface MondayBoard {
  id: string;
  name: string;
  description: string;
  boardKind: string;
  state: string;
  itemTerminology: string;
}

interface MondayColumnOption {
  id: string;
  label: string;
}

interface MondayColumn {
  id: string;
  title: string;
  type: string;
  options: MondayColumnOption[] | null;
}

type TabId =
  "overview" | "boards" | "field-mapping" | "ai-tools" | "data-mapping" | "log";

// Standard Velaro-side fields a customer might want to route into a Monday.com
// custom column — lead capture (Push Lead skill) reads this mapping when it's
// present instead of only setting the item name.
const VELARO_FIELD_KEYS: { key: string; label: string; hint: string }[] = [
  { key: "contactName", label: "Contact Name", hint: "Visitor/lead full name" },
  {
    key: "contactEmail",
    label: "Contact Email",
    hint: "Visitor/lead email address"
  },
  {
    key: "contactPhone",
    label: "Contact Phone",
    hint: "Visitor/lead phone number"
  },
  { key: "company", label: "Company", hint: "Visitor/lead company name" },
  {
    key: "leadSource",
    label: "Lead Source",
    hint: "Channel/campaign the lead came from"
  },
  {
    key: "status",
    label: "Status",
    hint: "Pipeline/status stage — maps best to a Status column"
  }
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS: {
  name: string;
  label: string;
  group: string;
  description: string;
  destructive?: boolean;
}[] = [
  {
    name: "monday_push_lead",
    label: "Push Lead",
    group: "Lead Capture",
    description:
      "Capture visitor name, email, and phone and create a lead item on the default Monday.com board."
  },
  {
    name: "monday_search_items",
    label: "Search Items",
    group: "Items",
    description:
      "Search for items (people, leads, projects) across boards by keyword."
  },
  {
    name: "monday_get_item",
    label: "Get Item",
    group: "Items",
    description:
      "Get full details for a specific board item by ID — columns, status, assignees, and history."
  },
  {
    name: "monday_create_item",
    label: "Create Item",
    group: "Items",
    description:
      "Create a new item (lead, contact, task) on a board with initial column values."
  },
  {
    name: "monday_update_item",
    label: "Update Item",
    group: "Items",
    description: "Update one or more column values on an existing board item."
  },
  {
    name: "monday_get_column_values",
    label: "Get Column Values",
    group: "Items",
    description: "Get all column values for a specific board item."
  },
  {
    name: "monday_move_item",
    label: "Move Item",
    group: "Items",
    description:
      "Move a board item to a different group (pipeline stage, status category)."
  },
  {
    name: "monday_archive_item",
    label: "Archive Item",
    group: "Items",
    description: "Archive a completed or resolved item from the board."
  },
  {
    name: "monday_set_status",
    label: "Set Status",
    group: "Items",
    description:
      "Set the status column of a board item to a new value (e.g. Done, In Progress, Stuck). Shortcut for the most common Monday action."
  },
  {
    name: "monday_assign_user",
    label: "Assign User",
    group: "Items",
    description: "Assign a team member to a board item by user ID."
  },
  {
    name: "monday_delete_item",
    label: "Delete Item",
    group: "Items",
    description:
      "Permanently delete a board item. Requires explicit visitor confirmation before executing.",
    destructive: true
  },
  {
    name: "monday_create_update",
    label: "Post Update",
    group: "Updates",
    description: "Post a timeline update or comment to a board item."
  },
  {
    name: "monday_get_updates",
    label: "Get Updates",
    group: "Updates",
    description: "Get recent updates and comments on a board item."
  },
  {
    name: "monday_get_board",
    label: "Get Board",
    group: "Boards",
    description: "Get board structure — columns, groups, and configuration."
  },
  {
    name: "monday_list_boards",
    label: "List Boards",
    group: "Boards",
    description:
      "List all available Monday.com boards with their IDs and names."
  },
  {
    name: "monday_create_subitem",
    label: "Create Subitem",
    group: "Subitems",
    description: "Create a subitem (child task) under an existing board item."
  },
  {
    name: "monday_get_subitems",
    label: "Get Subitems",
    group: "Subitems",
    description: "Get all subitems for a board item."
  },
  {
    name: "monday_get_users",
    label: "Get Users",
    group: "Users",
    description: "Get list of board members and users available for assignment."
  },
  {
    name: "monday_notify_user",
    label: "Notify User",
    group: "Users",
    description:
      "Send an in-app Monday.com notification to a team member about a board item."
  }
];

// ─── Shared UI ────────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
      viewBox="0 0 16 16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
    </svg>
  );
}

// ─── Find Token Accordion ─────────────────────────────────────────────────────

function FindTokenAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-zinc-50 hover:bg-zinc-100 text-sm font-medium text-zinc-700"
        aria-expanded={open}
      >
        <span>Where to find your Personal API Token</span>
        <Chevron open={open} />
      </button>
      {open && (
        <>
          <Divider className="my-0" />
          <div className="px-4 pb-4 pt-3 text-sm text-zinc-600 space-y-1.5 bg-white">
            <ol className="list-none space-y-2">
              {[
                "Log in to Monday.com",
                "Click your avatar (top-right) → Profile",
                "Scroll to the bottom → Developers section",
                "Click 'My access tokens' → copy the API v2 token"
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className="shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: MONDAY_COLOR }}
                  >
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

// ─── OAuthConnectPanel ────────────────────────────────────────────────────────

function OAuthConnectPanel({ oauthError }: { oauthError: string | null }) {
  const api = useApi();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(oauthError);

  async function handleOAuthConnect() {
    setConnecting(true);
    setError(null);
    try {
      const resp = await api.admin.get("Monday/OAuth/AuthUrl");
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
        };
        setError(
          body?.error ?? "Failed to start OAuth flow. Please try again."
        );
        return;
      }
      const data = (await resp.json()) as { authUrl: string };
      window.location.href = data.authUrl;
    } catch {
      setError("Failed to start OAuth flow. Please try again.");
    } finally {
      setConnecting(false);
    }
  }

  return (
    <SectionCard
      title="Connect with Monday.com"
      description="Recommended — secure OAuth 2.0, no token to manage"
      className="mb-4"
    >
      <p className="text-sm text-zinc-500 mb-5">
        You'll be redirected to Monday.com to authorize Velaro, then brought
        back automatically. Tokens are long-lived — nothing expires on you.
      </p>
      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
      <div className="flex justify-end">
        {connecting ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <svg
              className="animate-spin w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
            Redirecting to Monday.com…
          </div>
        ) : (
          <PrimaryButton
            onClick={handleOAuthConnect}
            style={{ backgroundColor: MONDAY_COLOR }}
          >
            Connect with Monday.com →
          </PrimaryButton>
        )}
      </div>
    </SectionCard>
  );
}

// ─── ConnectForm ──────────────────────────────────────────────────────────────

interface ConnectFormState {
  apiToken: string;
  accountSlug: string;
  displayName: string;
  defaultBoardId: string;
  pushLeadsToBoard: boolean;
  pushTranscripts: boolean;
  saving: boolean;
  testing: boolean;
  error: string | null;
  testResult: { success: boolean; message: string } | null;
}

function ConnectForm({
  existingConfig,
  onSaved
}: {
  existingConfig: MondayConfig | null;
  onSaved(cfg: MondayConfig): void;
}) {
  const api = useApi();
  const [form, setForm] = useState<ConnectFormState>({
    apiToken: "",
    accountSlug: existingConfig?.accountSlug ?? "",
    displayName: existingConfig?.displayName ?? "",
    defaultBoardId: existingConfig?.defaultBoardId ?? "",
    pushLeadsToBoard: existingConfig?.pushLeadsToBoard ?? true,
    pushTranscripts: existingConfig?.pushTranscripts ?? false,
    saving: false,
    testing: false,
    error: null,
    testResult: null
  });

  async function handleTest() {
    if (!form.apiToken.trim()) {
      setForm((f) => ({ ...f, error: "Enter an API Token first." }));
      return;
    }
    setForm((f) => ({ ...f, testing: true, testResult: null, error: null }));
    try {
      const resp = await api.admin.post("Monday/test", {
        apiToken: form.apiToken.trim()
      });
      const d = (await resp.json().catch(() => ({}))) as {
        valid?: boolean;
        error?: string;
        userName?: string;
        accountName?: string;
      };
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: {
          success: d.valid ?? false,
          message: d.valid
            ? `Connected${d.userName ? ` as ${d.userName}` : ""}${d.accountName ? ` — ${d.accountName}` : ""}`
            : (d.error ?? "Connection test failed.")
        }
      }));
    } catch (ex) {
      console.error("Monday test failed", ex);
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: { success: false, message: "Network error during test." }
      }));
    }
  }

  async function handleSave() {
    if (!form.apiToken.trim() && !existingConfig?.isConnected) {
      setForm((f) => ({ ...f, error: "API Token is required." }));
      return;
    }
    setForm((f) => ({ ...f, saving: true, error: null }));
    try {
      const payload: Record<string, unknown> = {
        accountSlug: form.accountSlug.trim() || null,
        displayName: form.displayName.trim() || null,
        defaultBoardId: form.defaultBoardId.trim() || null,
        pushLeadsToBoard: form.pushLeadsToBoard,
        pushTranscripts: form.pushTranscripts
      };
      if (form.apiToken.trim()) payload.apiToken = form.apiToken.trim();

      // Editing an already-connected config without entering a new token is a
      // partial update — use PUT, which never requires (and never touches) the
      // stored ApiToken. POST always requires + validates a token, so it's only
      // used for the initial connect or an explicit token rotation.
      const isPartialUpdate =
        existingConfig?.isConnected && !form.apiToken.trim();
      const resp = isPartialUpdate
        ? await api.admin.put("Monday", payload)
        : await api.admin.post("Monday", payload);
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setForm((f) => ({
          ...f,
          saving: false,
          error: (d as { error?: string }).error ?? "Failed to save."
        }));
        return;
      }
      const savedConfig = (await resp.json()) as MondayConfig;
      setForm((f) => ({ ...f, saving: false, apiToken: "" }));
      onSaved(savedConfig);
    } catch (ex) {
      console.error("Monday save failed", ex);
      setForm((f) => ({
        ...f,
        saving: false,
        error: "Network error — please try again."
      }));
    }
  }

  return (
    <SectionCard
      title={
        existingConfig?.isConnected ? "Update Connection" : "Connect Monday.com"
      }
      className="mb-4"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1">
            Personal API Token{" "}
            {!existingConfig?.isConnected && (
              <span className="text-red-500">*</span>
            )}
            {existingConfig?.isConnected && (
              <span className="text-zinc-400 ml-1">
                (leave blank to keep existing)
              </span>
            )}
          </label>
          <input
            type="password"
            value={form.apiToken}
            onChange={(e) =>
              setForm((f) => ({ ...f, apiToken: e.target.value }))
            }
            placeholder={
              existingConfig?.isConnected
                ? "Enter new token to update"
                : "eyJ..."
            }
            autoComplete="new-password"
            className="w-full text-sm px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono"
          />
          <FindTokenAccordion />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-zinc-600 mb-1">
              Account Slug <span className="text-zinc-400">(optional)</span>
            </label>
            <input
              type="text"
              value={form.accountSlug}
              onChange={(e) =>
                setForm((f) => ({ ...f, accountSlug: e.target.value }))
              }
              placeholder="e.g. mycompany"
              className="w-full text-sm px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-600 mb-1">
              Display Name <span className="text-zinc-400">(optional)</span>
            </label>
            <FieldInput
              type="text"
              value={form.displayName}
              onChange={(e) =>
                setForm((f) => ({ ...f, displayName: e.target.value }))
              }
              placeholder="e.g. Our Monday Workspace"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-zinc-600 mb-1">
            Default Board ID{" "}
            <span className="text-zinc-400">(for lead capture)</span>
          </label>
          <FieldInput
            type="text"
            value={form.defaultBoardId}
            onChange={(e) =>
              setForm((f) => ({ ...f, defaultBoardId: e.target.value }))
            }
            placeholder="Numeric board ID, e.g. 1234567890"
            className="font-mono"
          />
          <p className="text-xs text-zinc-400 mt-1">
            Found in the board URL: monday.com/boards/
            <strong>1234567890</strong>
          </p>
        </div>

        <div className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.pushLeadsToBoard}
              onChange={(e) =>
                setForm((f) => ({ ...f, pushLeadsToBoard: e.target.checked }))
              }
              className="rounded border-zinc-300 text-blue-600"
            />
            <div>
              <div className="text-sm font-medium text-zinc-700">
                Push Leads to Board
              </div>
              <div className="text-xs text-zinc-400">
                Automatically create items when monday_push_lead is triggered
              </div>
            </div>
          </label>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.pushTranscripts}
              onChange={(e) =>
                setForm((f) => ({ ...f, pushTranscripts: e.target.checked }))
              }
              className="rounded border-zinc-300 text-blue-600"
            />
            <div>
              <div className="text-sm font-medium text-zinc-700">
                Push Transcripts
              </div>
              <div className="text-xs text-zinc-400">
                Post resolved conversation transcripts as updates on matched
                board items
              </div>
            </div>
          </label>
        </div>

        {form.error && (
          <div
            className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-800"
            role="alert"
          >
            {form.error}
          </div>
        )}
        {form.testResult && (
          <div
            className={`rounded-lg p-3 text-sm ${form.testResult.success ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}
            role="status"
          >
            {form.testResult.message}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap pt-1">
          <SecondaryButton
            onClick={handleTest}
            disabled={form.testing || form.saving}
            className="!text-xs !px-3 !py-1.5"
          >
            {form.testing ? "Testing…" : "Test Connection"}
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSave}
            disabled={form.saving || form.testing}
            className="!px-4 !py-1.5 !text-xs"
            style={{ backgroundColor: MONDAY_COLOR }}
          >
            {form.saving
              ? "Saving…"
              : existingConfig?.isConnected
                ? "Update"
                : "Connect"}
          </PrimaryButton>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── LegacyTokenAccordion ─────────────────────────────────────────────────────

function LegacyTokenAccordion({
  existingConfig,
  onSaved
}: {
  existingConfig: MondayConfig | null;
  onSaved(cfg: MondayConfig): void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded border border-zinc-200 bg-zinc-50 overflow-hidden mb-4">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3 text-sm text-zinc-400 hover:text-zinc-500 transition"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="flex items-center gap-2">
          <span className="text-xs font-medium bg-zinc-200 text-zinc-500 px-2 py-0.5 rounded">
            Legacy
          </span>
          Personal API Token
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <>
          <Divider className="my-0" />
          <div className="bg-white">
            <div className="px-5 pt-3 pb-1">
              <p className="text-xs text-zinc-400 mb-3">
                Personal API Tokens work but require manual rotation. Use OAuth
                above unless you have a specific reason to use a token.
              </p>
            </div>
            <ConnectForm existingConfig={existingConfig} onSaved={onSaved} />
          </div>
        </>
      )}
    </div>
  );
}

// ─── BoardSettingsCard ──────────────────────────────────────────────────────

// QA-Report-Monday-Retest-4 (2026-07-13): monday_push_lead kept failing with
// "Make sure a default board ID is configured" even after the connection moved
// to OAuth, because the ONLY place Default Board ID could be set was inside the
// "Legacy Personal API Token" accordion's ConnectForm — invisible/discouraged
// for OAuth-first users who never open it. This card surfaces the same field
// directly on the connected Overview state, for BOTH auth methods, via the
// existing PUT /Monday partial-update path (no token required).
function BoardSettingsCard({
  config,
  onRefresh
}: {
  config: MondayConfig;
  onRefresh(): void;
}) {
  const api = useApi();
  const [boardId, setBoardId] = useState(config.defaultBoardId ?? "");
  const [groupId, setGroupId] = useState(config.defaultGroupId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setBoardId(config.defaultBoardId ?? "");
    setGroupId(config.defaultGroupId ?? "");
  }, [config.defaultBoardId, config.defaultGroupId]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // Send "" rather than null when cleared — MondayController's PUT treats null as "field
      // omitted, leave unchanged" but "" as an explicit clear (Opus review, 2026-07-13: sending
      // null here made clearing Default Group ID silently bounce back to its old value).
      const resp = await api.admin.put("Monday", {
        defaultBoardId: boardId.trim(),
        defaultGroupId: groupId.trim()
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
        };
        setError(body?.error ?? `Failed to save (HTTP ${resp.status}).`);
        return;
      }
      setSaved(true);
      onRefresh();
    } catch (ex) {
      console.error("Monday board settings save failed", ex);
      setError("Network error — please try again.");
    } finally {
      setSaving(false);
    }
  }

  const dirty =
    boardId.trim() !== (config.defaultBoardId ?? "") ||
    groupId.trim() !== (config.defaultGroupId ?? "");

  return (
    <SectionCard
      title="Board Settings"
      description="Required for Push Lead — the board where captured leads and auto-provisioned transcript items are created."
      className="mb-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="monday-default-board-id"
            className="block text-xs font-medium text-zinc-600 mb-1"
          >
            Default Board ID <span className="text-red-500">*</span>
          </label>
          <input
            id="monday-default-board-id"
            type="text"
            value={boardId}
            onChange={(e) => {
              setBoardId(e.target.value);
              setSaved(false);
            }}
            placeholder="Numeric board ID, e.g. 1234567890"
            className="w-full text-sm px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono"
          />
          <p className="text-xs text-zinc-600 mt-1">
            Found in the board URL: monday.com/boards/
            <strong>1234567890</strong>. See the Boards tab for a list of boards
            you can connect.
          </p>
        </div>
        <div>
          <label
            htmlFor="monday-default-group-id"
            className="block text-xs font-medium text-zinc-600 mb-1"
          >
            Default Group ID <span className="text-zinc-400">(optional)</span>
          </label>
          <input
            id="monday-default-group-id"
            type="text"
            value={groupId}
            onChange={(e) => {
              setGroupId(e.target.value);
              setSaved(false);
            }}
            placeholder="e.g. topics"
            className="w-full text-sm px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white font-mono"
          />
        </div>
      </div>
      {!config.defaultBoardId && (
        <div
          className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800"
          role="alert"
        >
          No default board set — Push Lead cannot create items until a board ID
          is saved here.
        </div>
      )}
      {error && (
        <div
          className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700"
          role="alert"
        >
          {error}
        </div>
      )}
      {saved && !error && (
        <div
          className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700"
          role="status"
        >
          Board settings saved.
        </div>
      )}
      <div className="flex justify-end mt-3">
        <PrimaryButton
          onClick={handleSave}
          disabled={saving || !dirty || !boardId.trim()}
          className="!px-4 !py-1.5 !text-xs"
          style={{ backgroundColor: MONDAY_COLOR }}
        >
          {saving ? "Saving…" : "Save Board Settings"}
        </PrimaryButton>
      </div>
    </SectionCard>
  );
}

// ─── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({
  config,
  onRefresh,
  onOpenWizard,
  oauthError
}: {
  config: MondayConfig | null;
  onRefresh(): void;
  onOpenWizard(): void;
  oauthError: string | null;
}) {
  const api = useApi();
  const [removing, setRemoving] = useState(false);
  const [togglingTranscripts, setTogglingTranscripts] = useState(false);
  const [transcriptToggleError, setTranscriptToggleError] = useState<
    string | null
  >(null);

  async function handleRemove() {
    if (!confirm("Remove Monday.com connection? This cannot be undone."))
      return;
    setRemoving(true);
    try {
      await api.admin.delete("Monday");
      onRefresh();
    } catch (ex) {
      console.error("Monday remove failed", ex);
    } finally {
      setRemoving(false);
    }
  }

  async function togglePushTranscripts() {
    if (!config) return;
    setTogglingTranscripts(true);
    setTranscriptToggleError(null);
    try {
      const resp = await api.admin.put("Monday", {
        pushTranscripts: !config.pushTranscripts
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
        };
        setTranscriptToggleError(
          body?.error ?? `Failed to update (HTTP ${resp.status}).`
        );
        return;
      }
      onRefresh();
    } catch (ex) {
      console.error("Monday transcript toggle failed", ex);
      setTranscriptToggleError("Network error — please try again.");
    } finally {
      setTogglingTranscripts(false);
    }
  }

  return (
    <div>
      {/* How it works */}
      <SectionCard
        title="How Monday.com works in Velaro"
        description="Connect once — every board, item, and update becomes AI-accessible during any conversation."
        className="mb-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4 relative">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0"
                style={{ backgroundColor: MONDAY_COLOR }}
              >
                1
              </span>
              <span className="font-semibold text-zinc-700 text-sm">
                Connect Monday.com
              </span>
            </div>
            <p className="text-xs text-zinc-500">
              Paste your Personal API Token — found in Monday.com Profile →
              Developers.
            </p>
            <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
              <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
                <path
                  d="M3 2L7 5L3 8"
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 relative">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                2
              </span>
              <span className="font-semibold text-green-800 text-sm">
                19 AI tools activate
              </span>
            </div>
            <p className="text-xs text-green-700">
              Lead capture, item management, board lookups, updates — all
              AI-accessible in real time.
            </p>
            <div className="hidden sm:flex absolute -right-3.5 top-1/2 -translate-y-1/2 z-10 w-7 h-7 rounded-full bg-white border border-zinc-200 shadow items-center justify-center">
              <svg viewBox="0 0 10 10" width="10" height="10" fill="none">
                <path
                  d="M3 2L7 5L3 8"
                  stroke="#94a3b8"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-(--ds-primary) text-white text-xs font-bold flex items-center justify-center shrink-0">
                3
              </span>
              <span className="font-semibold text-blue-800 text-sm">
                AI-powered project ops
              </span>
            </div>
            <p className="text-xs text-blue-700">
              Agents see full board context during every chat — create leads,
              post updates, move items.
            </p>
          </div>
        </div>
      </SectionCard>

      {config?.isConnected ? (
        <>
          {/* Connected status card */}
          <SectionCard className="mb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                  style={{ backgroundColor: MONDAY_COLOR }}
                >
                  M
                </div>
                <div>
                  <div className="font-semibold text-zinc-800 flex items-center gap-2">
                    {config.displayName ?? "Monday.com"}
                    {config.authMethod === "oauth" && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                        OAuth
                      </span>
                    )}
                  </div>
                  {config.accountSlug && (
                    <div className="text-xs text-zinc-400">
                      Account: {config.accountSlug}
                    </div>
                  )}
                  {config.connectedAs && (
                    <div className="text-xs text-zinc-400">
                      Connected as {config.connectedAs}
                    </div>
                  )}
                  {config.authMethod !== "oauth" && config.apiTokenMasked && (
                    <div className="text-xs text-zinc-400 font-mono">
                      Token: {config.apiTokenMasked}
                    </div>
                  )}
                  {config.oAuthConnectedAt && (
                    <div className="text-xs text-zinc-400">
                      Authorized{" "}
                      {new Date(config.oAuthConnectedAt).toLocaleDateString()}
                    </div>
                  )}
                  {config.defaultBoardId && (
                    <div className="text-xs text-zinc-400">
                      Default board: {config.defaultBoardId}
                    </div>
                  )}
                  <IntegrationHealthBadge slug="Monday" className="mt-1" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">
                  Connected
                </span>
                <GhostButton
                  onClick={handleRemove}
                  disabled={removing}
                  className="!text-xs !text-red-500 hover:!text-red-700 hover:!no-underline"
                >
                  {removing ? "Removing…" : "Disconnect"}
                </GhostButton>
              </div>
            </div>
          </SectionCard>

          {/* Board Settings — required for Push Lead, editable regardless of auth method */}
          <BoardSettingsCard config={config} onRefresh={onRefresh} />

          {/* Transcript Auto-Push */}
          <SectionCard title="Transcript Auto-Push" className="mb-4">
            <div className="text-sm text-zinc-500 mb-4">
              When enabled, resolved conversation transcripts are automatically
              posted as an update on the matched Monday.com board item.
            </div>
            <div className="flex items-center justify-between gap-4 p-3 border border-zinc-100 rounded-lg">
              <div>
                <div className="text-sm font-medium text-zinc-700">
                  {config.displayName ?? "Monday.com"}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {config.pushTranscripts
                    ? "Transcript posted as an update after each conversation"
                    : "Transcript push is off for this connection"}
                </div>
              </div>
              <button
                type="button"
                onClick={togglePushTranscripts}
                disabled={togglingTranscripts}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 disabled:opacity-50 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                aria-label={
                  config.pushTranscripts
                    ? "Disable transcript push"
                    : "Enable transcript push"
                }
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`}
                />
              </button>
            </div>
            {transcriptToggleError && (
              <div
                className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700"
                role="alert"
              >
                {transcriptToggleError}
              </div>
            )}
            {config.pushTranscripts && !transcriptToggleError && (
              <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                Auto-push active — transcripts will appear as updates on the
                matched Monday.com board item after each conversation.
              </div>
            )}
          </SectionCard>

          {/* Re-auth / update section */}
          <LegacyTokenAccordion
            existingConfig={config}
            onSaved={() => onRefresh()}
          />

          {/* Wizard card */}
          <SectionCard
            title="Launch Starter Workflow Wizard"
            description="Build a Monday.com bot in minutes — lead capture, project status updates, or support ticket tracking."
            className="mb-4"
            action={
              <PrimaryButton
                onClick={onOpenWizard}
                className="shrink-0"
                style={{ backgroundColor: MONDAY_COLOR }}
              >
                Launch Wizard →
              </PrimaryButton>
            }
          >
            <div />
          </SectionCard>
        </>
      ) : (
        <>
          <OAuthConnectPanel oauthError={oauthError} />
          <LegacyTokenAccordion
            existingConfig={null}
            onSaved={() => onRefresh()}
          />
        </>
      )}
    </div>
  );
}

// ─── BoardsTab ────────────────────────────────────────────────────────────────

function BoardsTab({ isConnected }: { isConnected: boolean }) {
  const api = useApi();
  const [boards, setBoards] = useState<MondayBoard[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBoards = useCallback(async () => {
    if (!isConnected) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.admin.get("Monday/boards");
      if (!resp.ok) {
        setError("Failed to load boards.");
        return;
      }
      const data = await resp.json();
      setBoards(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("Monday boards fetch failed", ex);
      setError("Failed to load boards.");
    } finally {
      setLoading(false);
    }
  }, [api.admin, isConnected]);

  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  if (!isConnected) {
    return (
      <SectionCard>
        <div className="text-center text-zinc-400 py-4">
          <div className="flex justify-center mb-3 text-zinc-400">
            <FileText className="w-10 h-10" />
          </div>
          <div className="font-medium text-zinc-600 mb-1">
            Connect Monday.com first
          </div>
          <p className="text-sm">
            Your boards will appear here after you connect your Monday.com
            account.
          </p>
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Your Boards"
      action={
        <SecondaryButton
          type="button"
          onClick={loadBoards}
          disabled={loading}
          className="!text-xs !px-3 !py-1.5 !rounded-lg !border !border-zinc-200 !bg-white !text-zinc-700 hover:!bg-zinc-100"
        >
          {loading ? "Loading…" : "Refresh"}
        </SecondaryButton>
      }
    >
      {loading && (
        <div className="py-8 flex justify-center">
          <LoadingSpinner />
        </div>
      )}
      {error && (
        <div className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-700">
          {error}
        </div>
      )}
      {!loading && !error && boards.length === 0 && (
        <div className="text-center py-8 text-zinc-400">
          <div className="flex justify-center mb-2 text-zinc-400">
            <FileText className="w-8 h-8" />
          </div>
          <p className="text-sm">
            No boards found. Make sure your API token has board access.
          </p>
        </div>
      )}

      {!loading && boards.length > 0 && (
        <div className="space-y-2">
          {boards.map((b) => (
            <div
              key={b.id}
              className="rounded-xl border border-zinc-100 p-4 flex items-start gap-3"
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold shrink-0"
                style={{ backgroundColor: MONDAY_COLOR }}
              >
                M
              </div>
              <div className="min-w-0">
                <div className="font-medium text-zinc-800 text-sm">
                  {b.name}
                </div>
                {b.description && (
                  <div className="text-xs text-zinc-500 mt-0.5 truncate">
                    {b.description}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-[10px] font-mono text-zinc-400">
                    ID: {b.id}
                  </span>
                  {b.boardKind && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-100 text-zinc-500 capitalize">
                      {b.boardKind.replace(/_/g, " ")}
                    </span>
                  )}
                  {b.state && b.state !== "active" && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 capitalize">
                      {b.state}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── FieldMappingTab ────────────────────────────────────────────────────────
//
// Monday.com's own board UI lets a customer build a table with typed columns —
// status, people, dropdown, date, numbers, text — and every one of those columns
// is a first-class CRM field. Without this, Velaro only ever touches an item's
// name; the customer's actual custom columns are invisible. This tab surfaces a
// board's real columns (fetched fresh from GET /Monday/boards/{boardId}/columns,
// including parsed status/dropdown option labels) and lets the customer map each
// one to a standard Velaro field, persisted via GET/PUT /Monday/FieldMapping.

function FieldMappingTab({ config }: { config: MondayConfig | null }) {
  const api = useApi();
  const [columns, setColumns] = useState<MondayColumn[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const boardId = config?.defaultBoardId ?? null;
  const isConnected = config?.isConnected ?? false;

  const load = useCallback(async () => {
    if (!isConnected || !boardId) return;
    setLoading(true);
    setError(null);
    try {
      const [colsResp, mapResp] = await Promise.all([
        api.admin.get(`Monday/boards/${encodeURIComponent(boardId)}/columns`),
        api.admin.get("Monday/FieldMapping")
      ]);
      const colsData = colsResp.ok ? await colsResp.json() : [];
      setColumns(Array.isArray(colsData) ? colsData : []);
      if (mapResp.ok) {
        const mapData = (await mapResp.json()) as {
          mapping?: Record<string, string>;
        };
        setMapping(mapData?.mapping ?? {});
      }
    } catch (ex) {
      console.error("Monday field mapping load failed", ex);
      setError(
        `Failed to load columns: ${ex instanceof Error ? ex.message : String(ex)}`
      );
    } finally {
      setLoading(false);
    }
  }, [api.admin, boardId, isConnected]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const resp = await api.admin.put("Monday/FieldMapping", { mapping });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as {
          error?: string;
        };
        setError(body?.error ?? `Failed to save (HTTP ${resp.status}).`);
        return;
      }
      setSaved(true);
    } catch (ex) {
      console.error("Monday field mapping save failed", ex);
      setError("Network error — please try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!isConnected) {
    return (
      <SectionCard>
        <div className="text-center text-zinc-400 py-4">
          <div className="flex justify-center mb-3 text-zinc-400">
            <FileText className="w-10 h-10" />
          </div>
          <div className="font-medium text-zinc-600 mb-1">
            Connect Monday.com first
          </div>
          <p className="text-sm">
            Field mapping becomes available once you're connected and a default
            board is set.
          </p>
        </div>
      </SectionCard>
    );
  }

  if (!boardId) {
    return (
      <SectionCard>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
          Set a Default Board ID on the Overview tab first — field mapping is
          scoped to that board's columns.
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Field Mapping"
      description="Map Velaro fields to this board's real Monday.com columns (status, people, dropdown, date, numbers, text) so lead capture and AI tools write into the columns your team actually uses."
      action={
        <SecondaryButton
          type="button"
          onClick={load}
          disabled={loading}
          className="!text-xs !px-3 !py-1.5 !rounded-lg !border !border-zinc-200 !bg-white !text-zinc-700 hover:!bg-zinc-100"
        >
          {loading ? "Loading…" : "Refresh Columns"}
        </SecondaryButton>
      }
    >
      {error && (
        <div
          className="rounded-lg p-3 text-sm bg-red-50 border border-red-200 text-red-700 mb-4"
          role="alert"
        >
          {error}
        </div>
      )}
      {loading && columns.length === 0 && (
        <div className="py-8 flex justify-center">
          <LoadingSpinner />
        </div>
      )}

      {!loading && columns.length === 0 && !error && (
        <div className="text-center py-8 text-zinc-400">
          <p className="text-sm">No columns found on this board.</p>
        </div>
      )}

      {columns.length > 0 && (
        <div className="space-y-3">
          {VELARO_FIELD_KEYS.map((vf) => {
            const selected = mapping[vf.key] ?? "";
            const matchedColumn = columns.find((c) => c.id === selected);
            return (
              <div
                key={vf.key}
                className="flex items-center gap-3 rounded-lg border border-zinc-100 p-3"
              >
                <div className="w-40 shrink-0">
                  <div className="text-sm font-medium text-zinc-700">
                    {vf.label}
                  </div>
                  <div className="text-xs text-zinc-400">{vf.hint}</div>
                </div>
                <svg
                  viewBox="0 0 20 20"
                  width="14"
                  height="14"
                  fill="none"
                  className="shrink-0 text-zinc-300"
                >
                  <path
                    d="M5 10h10m0 0l-4-4m4 4l-4 4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex-1 min-w-0">
                  <label htmlFor={`monday-map-${vf.key}`} className="sr-only">
                    {vf.label} — Monday.com column
                  </label>
                  <FieldInput
                    as="select"
                    id={`monday-map-${vf.key}`}
                    value={selected}
                    onChange={(e) => {
                      setMapping((m) => ({ ...m, [vf.key]: e.target.value }));
                      setSaved(false);
                    }}
                  >
                    <option value="">— Not mapped —</option>
                    {columns.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title} ({c.type})
                      </option>
                    ))}
                  </FieldInput>
                  {matchedColumn?.options &&
                    matchedColumn.options.length > 0 && (
                      <div className="text-xs text-zinc-400 mt-1">
                        Options:{" "}
                        {matchedColumn.options.map((o) => o.label).join(", ")}
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {saved && !error && (
        <div
          className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700"
          role="status"
        >
          Field mapping saved.
        </div>
      )}

      <div className="flex justify-end mt-4">
        <PrimaryButton
          onClick={handleSave}
          disabled={saving || columns.length === 0}
          className="!px-4 !py-1.5 !text-xs"
          style={{ backgroundColor: MONDAY_COLOR }}
        >
          {saving ? "Saving…" : "Save Field Mapping"}
        </PrimaryButton>
      </div>
    </SectionCard>
  );
}

// ─── AiToolsTab ───────────────────────────────────────────────────────────────

function AiToolsTab({
  isConnected,
  onOpenWizard
}: {
  isConnected: boolean;
  onOpenWizard(): void;
}) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard
      title="AI Tools"
      description={`All ${AI_TOOLS.length} tools activate automatically on connect — no configuration required.`}
      action={
        isConnected ? <LaunchWizardButton onClick={onOpenWizard} /> : undefined
      }
    >
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`text-xs font-medium px-2 py-0.5 rounded-full ${isConnected ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
        >
          {isConnected ? `${AI_TOOLS.length} active` : "No connection"}
        </span>
      </div>
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
              {group}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div
                  key={tool.name}
                  className="rounded-lg border border-zinc-100 p-3"
                >
                  <div className="flex items-start gap-2">
                    <div
                      className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                      style={{ backgroundColor: MONDAY_COLOR }}
                    />
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-zinc-700">
                          {tool.label}
                        </span>
                        {tool.destructive && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                            Destructive
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-400 mb-1">
                        {tool.name}
                      </div>
                      <div className="text-xs text-zinc-500">
                        {tool.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Divider className="mt-8 mb-8" />
      <SkillControlsPanel integration="monday" skills={AI_TOOLS} />
    </SectionCard>
  );
}

// ─── DataMappingTab ───────────────────────────────────────────────────────────

const FIELD_REFERENCE = [
  {
    scope: "Item",
    scopeKey: "item",
    fields: [
      { field: "id", label: "Item ID", notes: "Unique Monday.com item ID" },
      { field: "name", label: "Item Name", notes: "Display name of the item" },
      {
        field: "board_id",
        label: "Board ID",
        notes: "Parent board identifier"
      },
      {
        field: "group_id",
        label: "Group ID",
        notes: "Current group (stage/status)"
      },
      { field: "state", label: "State", notes: "active / archived / deleted" },
      { field: "created_at", label: "Created At", notes: "ISO timestamp" },
      {
        field: "updated_at",
        label: "Updated At",
        notes: "Last modification time"
      }
    ]
  },
  {
    scope: "Column Value",
    scopeKey: "column",
    fields: [
      { field: "id", label: "Column ID", notes: "Column identifier key" },
      { field: "title", label: "Column Title", notes: "Display name" },
      { field: "value", label: "Column Value", notes: "Raw stored value" },
      {
        field: "type",
        label: "Column Type",
        notes:
          "text / email / phone / status / date / numbers / people / dropdown / link"
      }
    ]
  },
  {
    scope: "Update",
    scopeKey: "update",
    fields: [
      { field: "id", label: "Update ID", notes: "Unique ID for the update" },
      { field: "body", label: "Body", notes: "HTML or plain text content" },
      {
        field: "created_at",
        label: "Created At",
        notes: "Timestamp of the update"
      },
      {
        field: "creator.name",
        label: "Creator Name",
        notes: "Name of the user who posted"
      }
    ]
  },
  {
    scope: "Board",
    scopeKey: "board",
    fields: [
      { field: "id", label: "Board ID", notes: "Numeric board identifier" },
      { field: "name", label: "Board Name", notes: "Display name" },
      {
        field: "board_kind",
        label: "Board Kind",
        notes: "board / sub_items_board / document"
      },
      {
        field: "columns",
        label: "Columns",
        notes: "Array of column definitions"
      }
    ]
  }
];

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(`{${variable}}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ok */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded border font-mono transition-colors ${copied ? "border-green-300 bg-green-50 text-green-700" : "border-zinc-200 bg-zinc-50 text-zinc-500 hover:border-blue-300 hover:text-blue-700 hover:bg-blue-50"}`}
      aria-label={`Copy variable {${variable}}`}
    >
      {copied ? "✓" : `{${variable}}`}
    </button>
  );
}

function DataMappingTab() {
  const [activeScope, setActiveScope] = useState("Item");
  const scopeData =
    FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? FIELD_REFERENCE[0];

  return (
    <SectionCard
      title="Data Reference"
      description="Variable names for HTTP POST nodes to pass Monday.com data to external systems."
    >
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 mb-4 text-xs text-blue-800">
        <div className="font-semibold mb-2">Example HTTP POST variables</div>
        <CodeBlock>{`{
  "item_id":      "{monday.item.id}",
  "item_name":    "{monday.item.name}",
  "board_id":     "{monday.item.board_id}",
  "group_id":     "{monday.item.group_id}",
  "item_state":   "{monday.item.state}",
  "created_at":   "{monday.item.created_at}"
}`}</CodeBlock>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {FIELD_REFERENCE.map((s) => (
          <button
            key={s.scope}
            type="button"
            onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeScope === s.scope ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            style={
              activeScope === s.scope ? { backgroundColor: MONDAY_COLOR } : {}
            }
          >
            {s.scope}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div
          className="px-4 py-2.5 text-white text-xs font-semibold"
          style={{ backgroundColor: MONDAY_COLOR }}
        >
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Field
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Label
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Variable
                </th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">
                  Notes
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr
                  key={i}
                  className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}
                >
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">
                    {f.field}
                  </td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2">
                    <CopyVariableButton
                      variable={`monday.${scopeData.scopeKey}.${f.field}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-zinc-400 italic">{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Monday() {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState<MondayConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);

  // OAuth callback params — consumed once, then cleared from URL
  const oauthConnected = searchParams.get("connected") === "true";
  const oauthErrorParam = searchParams.get("error");

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await api.admin.get("Monday");
      const data = await resp.json();
      setConfig(data?.isConnected === false ? null : (data as MondayConfig));
    } catch (ex) {
      console.error("Monday fetchConfig failed", ex);
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // On return from OAuth, clear query params after loading
  useEffect(() => {
    if (!loading && (oauthConnected || oauthErrorParam)) {
      if (oauthConnected) {
        ensureDefaultRoutingWorkflow(api.messaging);
      }
      setSearchParams({}, { replace: true });
    }
  }, [loading, oauthConnected, oauthErrorParam, setSearchParams]);

  const isConnected = config?.isConnected ?? false;

  if (loading) return <RouteSkeleton />;

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "boards"}
        onClick={() => setActiveTab("boards")}
      >
        Boards
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "field-mapping"}
        onClick={() => setActiveTab("field-mapping")}
      >
        Field Mapping
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "ai-tools"}
        onClick={() => setActiveTab("ai-tools")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "data-mapping"}
        onClick={() => setActiveTab("data-mapping")}
      >
        Data Mapping
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "log"}
        onClick={() => setActiveTab("log")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Monday.com" tabs={tabsJSX}>
      {showWizard && (
        <StarterWorkflowWizard
          integration="monday"
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* OAuth return banners */}
      {oauthConnected && (
        <div className="rounded-xl p-3 mb-4 bg-green-50 border border-green-200 text-green-800 text-sm font-medium flex items-center gap-2">
          <svg
            viewBox="0 0 20 20"
            className="w-4 h-4 text-green-600 shrink-0"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          Monday.com connected successfully via OAuth.
        </div>
      )}
      {oauthErrorParam && (
        <div className="rounded-xl p-3 mb-4 bg-red-50 border border-red-200 text-red-800 text-sm font-medium">
          Monday.com OAuth error: {oauthErrorParam.replace(/_/g, " ")}. Please
          try again.
        </div>
      )}

      {/* Connection status banner */}
      <div
        className={`rounded-2xl p-4 mb-4 flex items-center justify-between gap-4 ${isConnected ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{ backgroundColor: MONDAY_COLOR }}
          >
            M
          </div>
          <div>
            <div
              className={`text-sm font-semibold ${isConnected ? "text-green-800" : "text-amber-800"}`}
            >
              {isConnected
                ? `Connected — ${config?.displayName ?? "Monday.com"}`
                : "Not Connected"}
            </div>
            <div
              className={`text-xs ${isConnected ? "text-green-600" : "text-amber-600"}`}
            >
              {isConnected
                ? `${AI_TOOLS.length} AI tools active · Lead capture enabled`
                : `Connect your Monday.com account to enable ${AI_TOOLS.length} AI tools`}
            </div>
          </div>
        </div>
        {isConnected && (
          <SecondaryButton
            onClick={() => {
              setActiveTab("overview");
            }}
            className="!text-xs !px-3 !py-1.5 !border !border-green-300 !bg-transparent !text-green-700 hover:!bg-green-100"
          >
            Manage
          </SecondaryButton>
        )}
      </div>

      {activeTab === "overview" && (
        <OverviewTab
          config={config}
          onRefresh={fetchConfig}
          onOpenWizard={() => setShowWizard(true)}
          oauthError={oauthErrorParam}
        />
      )}
      {activeTab === "boards" && <BoardsTab isConnected={isConnected} />}
      {activeTab === "field-mapping" && <FieldMappingTab config={config} />}
      {activeTab === "ai-tools" && (
        <AiToolsTab
          isConnected={isConnected}
          onOpenWizard={() => setShowWizard(true)}
        />
      )}
      {activeTab === "data-mapping" && <DataMappingTab />}
      {activeTab === "log" && (
        <SectionCard title="Activity Log">
          <IntegrationActivityLog integrationSlug="Monday" />
        </SectionCard>
      )}
    </StickyRouteHeader>
  );
}
